import { useCallback } from 'react'
import toast from 'react-hot-toast'
import areaApi from '@/api/config/areaApi'
import useTranslation from '@/hooks/useTranslation'
import type { GridCellValueChangedEvent } from '@/components/grid/types'
import {
  NON_IRRIGATION_EDITABLE_FIELDS,
  type StationRow,
} from './column-defs-builder'

interface UseCellValueChangedParams {
  /** The rows as displayed (base ⊕ pending edits). */
  getMergedRows: () => StationRow[]
  areaOptionsData: any[]
  interfaceId: string | number | null
  setSubAreaOptionsData: React.Dispatch<React.SetStateAction<any[]>>
  subAreaCacheRef: React.MutableRefObject<Record<number, any[]>>
  isNonIrrigation: (row: any) => boolean
  clearCellHighlight: (rowId: string, field: string) => void
}

/**
 * Guards an edit before the grid stages it, and runs the one side effect an
 * edit can have.
 *
 * The values themselves are staged by each column's `valueSetter`: one pick
 * writes the label *and* the id the save payload needs, into the grid's pending
 * -edit buffer. This hook only rejects edits that must not land (returning
 * `false` reverts the cell) and loads the sub-area list a new area brings with
 * it.
 */
export const useCellValueChanged = ({
  getMergedRows,
  areaOptionsData,
  interfaceId,
  setSubAreaOptionsData,
  subAreaCacheRef,
  isNonIrrigation,
  clearCellHighlight,
}: UseCellValueChangedParams) => {
  const { t } = useTranslation()

  const loadSubAreasForArea = useCallback(
    async (area: any) => {
      try {
        if (!subAreaCacheRef.current[area.id]) {
          subAreaCacheRef.current[area.id] = await areaApi
            .getSubAreaParent(Number(interfaceId), area.id)
            .then((response) => response.data)
            .catch(() => [])
        }

        setSubAreaOptionsData((previous) => [
          ...previous.filter((entry) => entry.areaName !== area.name),
          { areaName: area.name, subAreas: subAreaCacheRef.current[area.id] },
        ])
      } catch (error) {
        console.error('Failed to load sub areas:', error)
      }
    },
    [interfaceId, setSubAreaOptionsData, subAreaCacheRef],
  )

  const onCellValueChanged = useCallback(
    (event: GridCellValueChangedEvent<StationRow>): void | false => {
      const { field, rowId, row, newValue } = event

      clearCellHighlight(rowId, field)

      // Non-irrigation stations carry no irrigation data. The same list the
      // columns use to decide what is editable: a second copy here once left
      // out the station name, so a rename was dropped without a word.
      if (isNonIrrigation(row) && !NON_IRRIGATION_EDITABLE_FIELDS.includes(field)) {
        return false
      }

      if (field === 'decoderAddress') {
        const address = String(newValue ?? '')
        if (address.includes('-')) {
          const takenByAnother = getMergedRows().some(
            (other) =>
              String(other.id) !== rowId &&
              other.decoderAddress === address &&
              (Number(other.twiId) || 0) === (Number(row?.twiId) || 0),
          )
          if (takenByAnother) {
            toast.error(t('stationsToastDecoderAddressTaken', { address }))
            return false
          }
        }
        return
      }

      if (field === 'area') {
        const selected = areaOptionsData.find((area) => area.name === newValue)
        if (selected) loadSubAreasForArea(selected)
      }
    },
    [
      areaOptionsData,
      clearCellHighlight,
      getMergedRows,
      isNonIrrigation,
      loadSubAreasForArea,
      t,
    ],
  )

  return { onCellValueChanged }
}
